
import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Automation, TriggerType, AutomationStep, Tool, PlanType,
  FieldCatalogEntry, Transformation, InputBinding, InputSourceType,
  FileMetadata, IntentType, OutputMode
} from '../types';
import { AI_TOOLS, PRICING_PLANS } from '../constants';

const INTENTS: { id: IntentType; name: string; icon: string; desc: string; supportedModes: OutputMode[] }[] = [
  { id: 'outreach_drafts', name: 'Text Outreach', icon: '✉️', desc: 'Personalized drafts for email/LinkedIn', supportedModes: ['text', 'email', 'webhook'] },
  { id: 'enriched_dataset', name: 'Data Enrichment', icon: '🧠', desc: 'Add intelligence columns to existing rows', supportedModes: ['json', 'csv', 'webhook'] },
  { id: 'structured_dataset', name: 'Structured Data', icon: '📄', desc: 'Clean, mapped rows and columns', supportedModes: ['json', 'csv'] },
  { id: 'summary_analysis', name: 'Analysis Report', icon: '📊', desc: 'High-level synthesis of findings', supportedModes: ['html', 'pdf', 'text'] },
  { id: 'api_output', name: 'API Payload', icon: '🌐', desc: 'Clean JSON for external systems', supportedModes: ['json', 'webhook'] },
  { id: 'dataset_update', name: 'Live Update', icon: '🔄', desc: 'Sync results back to your database', supportedModes: ['webhook'] }
];

const Automations: React.FC<{ activeToolIds: string[]; userPlan: PlanType }> = ({ activeToolIds, userPlan }) => {
  const planConfig = PRICING_PLANS[userPlan];
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isNamingOpen, setIsNamingOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [protocolName, setProtocolName] = useState('');

  // Builder State
  const [newAuto, setNewAuto] = useState<Partial<Automation>>({
    name: 'Autonomous Data Intelligence',
    steps: [],
    run_policy: { priority: 'normal', busy_policy: 'queue', max_runs_per_day: 24 }
  });

  // Review & Select Internal State
  const [searchQuery, setSearchQuery] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [sampleMode, setSampleMode] = useState('all');
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set(['row_1']));

  // Intent-filtered tools
  const activeTools = useMemo(() => {
    let list = AI_TOOLS.filter(t => activeToolIds.includes(t.id));
    if (newAuto.intent) {
      const intentConfig = INTENTS.find(i => i.id === newAuto.intent);
      list = list.filter(t => t.supportedOutputModes.some(m => intentConfig?.supportedModes.includes(m)));
    }
    return list;
  }, [activeToolIds, newAuto.intent]);

  // Derive final output options from the LAST tool in the chain
  const finalTool = useMemo(() => {
    if (!newAuto.steps || newAuto.steps.length === 0) return null;
    const lastStep = newAuto.steps[newAuto.steps.length - 1];
    return AI_TOOLS.find(t => t.id === lastStep.toolId);
  }, [newAuto.steps]);

  const availableOutputModes = useMemo(() => {
    if (!finalTool) return [];
    const intentConfig = INTENTS.find(i => i.id === newAuto.intent);
    return finalTool.supportedOutputModes.filter(m => intentConfig?.supportedModes.includes(m));
  }, [finalTool, newAuto.intent]);

  useEffect(() => {
    if (availableOutputModes.length > 0) {
      if (!newAuto.finalOutputMode || !availableOutputModes.includes(newAuto.finalOutputMode)) {
        setNewAuto(p => ({ ...p, finalOutputMode: availableOutputModes[0] }));
      }
    } else {
      setNewAuto(p => ({ ...p, finalOutputMode: undefined }));
    }
  }, [availableOutputModes]);

  const handleNext = () => setWizardStep(p => p + 1);
  const handleBack = () => setWizardStep(p => Math.max(1, p - 1));

  const addToolToChain = (tool: Tool) => {
    const stepId = `step_${(newAuto.steps?.length || 0) + 1}`;
    const newStep: AutomationStep = {
      id: stepId,
      toolId: tool.id,
      mapping: { transformations: [], input_bindings: [] }
    };
    setNewAuto(p => ({ ...p, steps: [...(p.steps || []), newStep] }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setTimeout(() => {
      const mockMetadata: FileMetadata = {
        name: file.name, size: file.size, type: file.type, rowCount: 1250,
        columns: ['company', 'website', 'email', 'phone', 'linkedin'],
        preview: [
          { id: 'row_1', company: 'Acme Foods', website: 'acmefoods.com', email: 'sales@acmefoods.com', phone: '+1 555 0133', linkedin: 'linkedin.com/company/acmefoods' },
          { id: 'row_2', company: 'North Market', website: 'northmarket.io', email: 'info@northmarket.io', phone: '', linkedin: 'linkedin.com/company/northmarket' }
        ]
      };
      setNewAuto(p => ({
        ...p,
        inputSource: { type: 'file_payload', config: { filename: file.name }, fileMetadata: mockMetadata, selectedFields: ['company', 'website', 'email'] }
      }));
      setIsUploading(false);
      handleNext();
    }, 1200);
  };

  const toggleField = (field: string) => {
    if (!newAuto.inputSource) return;
    const current = newAuto.inputSource.selectedFields;
    const next = current.includes(field) ? current.filter(f => f !== field) : [...current, field];
    setNewAuto(p => ({ ...p, inputSource: { ...p.inputSource!, selectedFields: next } }));
  };

  const toggleRow = (id: string) => {
    const next = new Set(selectedRowIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedRowIds(next);
  };

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-none mb-2">ShadowEngine Automations</h2>
          <p className="text-slate-500 text-sm">Design deterministic, intent-driven pipelines with autonomous bot capacity.</p>
        </div>
        <button
          onClick={() => setIsNamingOpen(true)}
          className="px-8 py-4 bg-indigo-600 text-white font-black text-xs rounded-2xl shadow-xl shadow-indigo-600/20 uppercase tracking-widest hover:scale-105 transition-all"
        >
          <span>🏗️ Design Protocol</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Fleet Capacity', value: `${planConfig.bots} Active Bots`, sub: '14 / 48 Runs Today' },
          { label: 'Pipeline Latency', value: '4.2s Avg', sub: 'Optimized Fidelity' },
          { label: 'Queue Depth', value: 'Idle', sub: 'Next Slot: Now' }
        ].map((stat, i) => (
          <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-3xl shadow-sm transition-all hover:border-indigo-500/30">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.label}</div>
            <div className="text-2xl font-black">{stat.value}</div>
            <p className="text-[9px] text-slate-500 mt-2 font-bold uppercase tracking-tight">{stat.sub}</p>
          </div>
        ))}
      </div>

      <div className="py-32 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl bg-slate-50/20">
        <div className="text-5xl mb-4">⚙️</div>
        <h3 className="text-sm font-black uppercase text-slate-400 tracking-widest">No Active Pipelines Found</h3>
        <p className="text-xs text-slate-400 mt-1">Initialize your first deterministic workflow via the architect wizard.</p>
      </div>

      {/* Protocol Naming Modal */}
      {isNamingOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-950 w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col">
            <div className="px-10 py-8 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/30 dark:bg-slate-900/30">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-indigo-600/20">📝</div>
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tight leading-none">Name Your Protocol</h2>
                  <p className="text-xs text-slate-500 mt-1 font-bold uppercase tracking-widest">Assign an identifier before design</p>
                </div>
              </div>
              <button onClick={() => setIsNamingOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 flex items-center justify-center text-xl transition-colors">✕</button>
            </div>

            <div className="p-12">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wider">Protocol Name</label>
                  <input
                    type="text"
                    value={protocolName}
                    onChange={(e) => setProtocolName(e.target.value)}
                    placeholder="e.g., Lead Enrichment Pipeline"
                    className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 rounded-2xl text-lg font-bold focus:outline-none focus:border-indigo-500 transition-all"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">Choose a descriptive name that identifies the purpose of this automation protocol. You can change it later.</p>
              </div>
            </div>

            <div className="px-10 py-8 border-t border-slate-200 dark:border-slate-800 flex justify-end items-center bg-slate-50/30 dark:bg-slate-900/30 space-x-4">
              <button
                onClick={() => setIsNamingOpen(false)}
                className="px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 dark:hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (protocolName.trim()) {
                    setNewAuto(p => ({ ...p, name: protocolName }));
                    setIsNamingOpen(false);
                    setWizardStep(1);
                    setIsWizardOpen(true);
                  }
                }}
                disabled={!protocolName.trim()}
                className="px-12 py-4 bg-indigo-600 text-white font-black text-xs rounded-2xl shadow-xl shadow-indigo-600/20 uppercase tracking-widest hover:scale-105 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Continue to Wizard
              </button>
            </div>
          </div>
        </div>
      )}

      {isWizardOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-950 w-full max-w-6xl h-[94vh] rounded-[3rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col">

            <div className="px-10 py-8 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/30 dark:bg-slate-900/30 shrink-0">
              <div className="flex items-center space-x-6">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-indigo-600/20">🏗️</div>
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tight leading-none mb-2">Automation Architect</h2>
                  <div className="flex items-center space-x-1.5">
                    {[1, 2, 3, 4, 5, 6, 7].map(s => (
                      <div key={s} className={`h-1.5 w-10 rounded-full transition-all duration-700 ${wizardStep >= s ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-800'}`}></div>
                    ))}
                  </div>
                </div>
              </div>
              <button onClick={() => setIsWizardOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-800 flex items-center justify-center text-xl transition-colors">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto p-12 bg-slate-50/20 dark:bg-slate-900/10">

              {wizardStep === 1 && (
                <div className="max-w-4xl mx-auto space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-black uppercase tracking-tight mb-2">1. When it runs</h3>
                    <p className="text-slate-500 text-sm">Define the timing signal. Triggers do not carry data.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[
                      { id: 'manual', name: 'Manual Run', icon: '🔘', d: 'Execute on user demand' },
                      { id: 'schedule', name: 'Schedule', icon: '🕒', d: 'Recurring time frequency' },
                      { id: 'webhook', name: 'Webhook', icon: '🔗', d: 'External system POST' },
                      { id: 'bot_message', name: 'Bot Message', icon: '🤖', d: 'Chat app command' },
                      { id: 'file_upload', name: 'File Upload', icon: '📂', d: 'New file detected' },
                      { id: 'condition', name: 'Condition', icon: '🎯', d: 'Internal data threshold' }
                    ].map(t => (
                      <button
                        key={t.id}
                        onClick={() => { setNewAuto(p => ({ ...p, trigger: { type: t.id as TriggerType } })); handleNext(); }}
                        className={`p-8 border-2 rounded-[2.5rem] text-left transition-all group ${newAuto.trigger?.type === t.id ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/20 shadow-xl' : 'border-slate-100 dark:border-slate-800 hover:border-indigo-400 bg-white dark:bg-slate-900/40'}`}
                      >
                        <div className="text-4xl mb-6 group-hover:scale-110 transition-transform">{t.icon}</div>
                        <div className="font-black text-xs uppercase tracking-widest mb-1">{t.name}</div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase leading-relaxed">{t.d}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {wizardStep === 2 && (
                <div className="max-w-4xl mx-auto space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-black uppercase tracking-tight mb-2">2. Where data comes from</h3>
                    <p className="text-slate-500 text-sm">Step 0: The deterministic origin of your pipeline payload.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { id: 'file_payload', name: 'File Payload', icon: '📋' },
                        { id: 'dataset_query', name: 'Dataset Query', icon: '🔍' },
                        { id: 'webhook_payload', name: 'Hook Payload', icon: '📦' },
                        { id: 'preset', name: 'Saved Presets', icon: '💾' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setNewAuto(p => ({ ...p, inputSource: { ...p.inputSource, type: opt.id as InputSourceType, config: {}, selectedFields: [] } }))}
                          className={`p-6 border-2 rounded-3xl flex flex-col items-center justify-center space-y-4 transition-all ${newAuto.inputSource?.type === opt.id ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/20 shadow-lg shadow-indigo-500/10' : 'border-slate-100 dark:border-slate-800'}`}
                        >
                          <span className="text-3xl">{opt.icon}</span>
                          <span className="text-[10px] font-black uppercase tracking-widest">{opt.name}</span>
                        </button>
                      ))}
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[3rem] p-12 flex flex-col justify-center shadow-inner min-h-[350px]">
                      {newAuto.inputSource?.type === 'file_payload' ? (
                        <div
                          className={`border-4 border-dashed rounded-[2.5rem] p-12 flex flex-col items-center justify-center text-center transition-all cursor-pointer ${isUploading ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200 dark:border-slate-800 hover:border-indigo-400'}`}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {isUploading ? (
                            <div className="flex flex-col items-center space-y-6">
                              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                              <p className="text-xs font-black uppercase text-indigo-600 tracking-[0.2em]">Ingesting Protocol...</p>
                            </div>
                          ) : (
                            <>
                              <div className="text-5xl mb-6">📄</div>
                              <h4 className="text-base font-black uppercase tracking-tight">Drop Source File</h4>
                              <p className="text-[10px] text-slate-500 mt-2 font-black uppercase tracking-widest">CSV / JSON / PDF</p>
                            </>
                          )}
                          <input type="file" ref={fileInputRef} className="hidden" accept=".csv,.json,.pdf" onChange={handleFileUpload} />
                        </div>
                      ) : (
                        <div className="text-center text-slate-400">
                          <p className="italic text-sm mb-4">Awaiting {newAuto.inputSource?.type || 'source'} configuration.</p>
                          <div className="inline-block px-4 py-2 bg-white dark:bg-slate-800 rounded-xl text-[10px] font-black uppercase tracking-widest border border-slate-200 dark:border-slate-800">Mock Config Ready</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: INTENT SELECTION IS NOW HANDLED IN REVIEW & SELECT (STEP 5) OR RETAINED FOR WIZARD FLOW */}
              {wizardStep === 3 && (
                <div className="max-w-5xl mx-auto space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-black uppercase tracking-tight mb-2">3. Goal Constraint</h3>
                    <p className="text-slate-500 text-sm italic">Intent guides tool selection. Format is determined by the final tool.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {INTENTS.map(intent => (
                      <button
                        key={intent.id}
                        onClick={() => { setNewAuto(p => ({ ...p, intent: intent.id, steps: [] })); handleNext(); }}
                        className={`p-10 border-2 rounded-[2.5rem] text-left transition-all ${newAuto.intent === intent.id ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/20 shadow-2xl' : 'border-slate-100 dark:border-slate-800 hover:border-indigo-400 bg-white dark:bg-slate-900/40'}`}
                      >
                        <div className="text-4xl mb-6">{intent.icon}</div>
                        <div className="font-black text-xs uppercase tracking-widest mb-2">{intent.name}</div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase leading-relaxed">{intent.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {wizardStep === 4 && (
                <div className="space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-black uppercase tracking-tight mb-2">4. Chain Protocol</h3>
                    <p className="text-slate-500 text-sm">Showing tools compatible with <b>{INTENTS.find(i => i.id === newAuto.intent)?.name}</b>.</p>
                  </div>

                  <div className="flex items-center justify-center space-x-6 overflow-x-auto pb-8 min-h-[120px]">
                    <div className="px-6 py-4 bg-slate-100 dark:bg-slate-800 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-center min-w-[160px]">
                      <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">STEP 0 (SOURCE)</div>
                      <div className="text-xs font-black uppercase truncate">{newAuto.inputSource?.fileMetadata?.name || 'Inbound'}</div>
                    </div>
                    {newAuto.steps?.map((s, i) => (
                      <React.Fragment key={s.id}>
                        <span className="text-slate-300 font-black text-2xl">→</span>
                        <div className="px-6 py-4 bg-indigo-600 text-white rounded-2xl flex items-center space-x-3 shadow-xl shadow-indigo-600/20 relative group">
                          <span className="text-xl">{AI_TOOLS.find(t => t.id === s.toolId)?.icon}</span>
                          <span className="text-xs font-black uppercase truncate max-w-[120px]">{AI_TOOLS.find(t => t.id === s.toolId)?.name}</span>
                        </div>
                      </React.Fragment>
                    ))}
                    <span className="text-slate-300 font-black text-2xl">→</span>
                    <div className="w-16 h-16 rounded-[1.5rem] border-2 border-dashed border-slate-300 dark:border-slate-800 flex flex-col items-center justify-center text-slate-300">
                      <span className="text-2xl font-light">+</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 border-t dark:border-slate-800 pt-10 max-w-5xl mx-auto">
                    {activeTools.map(tool => (
                      <button
                        key={tool.id}
                        onClick={() => addToolToChain(tool)}
                        className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] text-left hover:border-indigo-500 transition-all group shadow-sm"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">{tool.icon}</div>
                          <div>
                            <div className="text-[10px] font-black uppercase tracking-widest mb-1">{tool.name}</div>
                            <div className="text-[9px] text-slate-400 font-bold uppercase">{tool.creditCost} Credits</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 5: REVIEW & SELECT (REFINED UI) */}
              {wizardStep === 5 && (
                <section className="mx-review-select space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {/* Header */}
                  <div className="mx-card flex flex-col md:flex-row md:items-center justify-between gap-6 bg-gradient-to-r from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
                    <div className="mx-title">
                      <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Review & Select</h2>
                      <p className="text-sm text-slate-500 font-medium">
                        Confirm the dataset before the next tool. Select rows/columns, clean values, and lock your intent.
                      </p>
                    </div>
                    <div className="mx-actions flex items-center space-x-3">
                      <button type="button" className="mx-btn mx-btn-ghost">
                        Preview Raw
                      </button>
                      <button onClick={handleNext} type="button" className="mx-btn px-8 shadow-xl shadow-indigo-600/20">
                        Confirm & Continue →
                      </button>
                    </div>
                  </div>

                  {/* Controls Card */}
                  <div className="mx-card space-y-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="mx-field">
                        <label>Search Payload</label>
                        <input
                          type="search"
                          placeholder="Search rows…"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>

                      <div className="mx-field">
                        <label>Quick Filter</label>
                        <select value={quickFilter} onChange={(e) => setQuickFilter(e.target.value)}>
                          <option value="all">All rows</option>
                          <option value="selected">Selected only</option>
                          <option value="missing">Has missing values</option>
                          <option value="duplicates">Possible duplicates</option>
                        </select>
                      </div>

                      <div className="mx-field">
                        <label>Sampling</label>
                        <select value={sampleMode} onChange={(e) => setSampleMode(e.target.value)}>
                          <option value="all">Use all rows</option>
                          <option value="top_50">Top 50 rows</option>
                          <option value="random_50">Random 50 rows</option>
                        </select>
                      </div>

                      <div className="mx-field">
                        <label>Pipeline Intent</label>
                        <select
                          value={newAuto.intent}
                          onChange={(e) => setNewAuto(p => ({ ...p, intent: e.target.value as IntentType }))}
                        >
                          {INTENTS.map(i => (
                            <option key={i.id} value={i.id}>{i.icon} {i.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Column Selection Pills */}
                    <div className="mx-columns space-y-3">
                      <div className="flex items-center justify-between border-b dark:border-slate-800 pb-2">
                        <strong className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Available Schema Columns</strong>
                        <div className="mx-columns-actions flex items-center space-x-2">
                          <button
                            className="mx-link"
                            onClick={() => setNewAuto(p => ({ ...p, inputSource: { ...p.inputSource!, selectedFields: p.inputSource!.fileMetadata!.columns } }))}
                          >Select all</button>
                          <span className="mx-dot">•</span>
                          <button
                            className="mx-link"
                            onClick={() => setNewAuto(p => ({ ...p, inputSource: { ...p.inputSource!, selectedFields: [] } }))}
                          >Select none</button>
                        </div>
                      </div>

                      <div className="mx-pillbox">
                        {newAuto.inputSource?.fileMetadata?.columns.map(col => (
                          <label key={col} className={`mx-pill transition-all ${newAuto.inputSource?.selectedFields.includes(col) ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/20' : 'opacity-60 grayscale'}`}>
                            <input
                              type="checkbox"
                              checked={newAuto.inputSource?.selectedFields.includes(col)}
                              onChange={() => toggleField(col)}
                            />
                            <span className="uppercase tracking-tight">{col}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Data Grid Card */}
                  <div className="mx-card space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            checked={selectedRowIds.size === newAuto.inputSource?.fileMetadata?.preview.length}
                            onChange={() => {
                              if (selectedRowIds.size === newAuto.inputSource?.fileMetadata?.preview.length) setSelectedRowIds(new Set());
                              else setSelectedRowIds(new Set(newAuto.inputSource?.fileMetadata?.preview.map(r => r.id)));
                            }}
                          />
                          <span className="text-[10px] font-black uppercase text-slate-500 group-hover:text-slate-700 dark:group-hover:text-slate-300">Select all rows</span>
                        </label>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Showing {newAuto.inputSource?.fileMetadata?.preview.length} of {newAuto.inputSource?.fileMetadata?.rowCount.toLocaleString()} records
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button type="button" className="mx-btn mx-btn-ghost px-3">
                          Suggest Dedupe
                        </button>
                        <button type="button" className="mx-btn mx-btn-ghost px-3">
                          Suggest Cleaning
                        </button>
                        <button type="button" className="mx-btn mx-btn-ghost px-3">
                          View Schema
                        </button>
                      </div>
                    </div>

                    <div className="mx-table-wrap overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800 shadow-inner">
                      <table className="mx-table">
                        <thead>
                          <tr className="bg-slate-50/50 dark:bg-slate-900/50">
                            <th className="w-12 text-center"></th>
                            {newAuto.inputSource?.fileMetadata?.columns.map(col => (
                              <th key={col} className={newAuto.inputSource?.selectedFields.includes(col) ? 'text-indigo-600 dark:text-indigo-400' : 'opacity-40'}>
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {newAuto.inputSource?.fileMetadata?.preview.map((row: any) => (
                            <tr key={row.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${selectedRowIds.has(row.id) ? 'bg-indigo-50/20 dark:bg-indigo-900/10' : ''}`}>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                  checked={selectedRowIds.has(row.id)}
                                  onChange={() => toggleRow(row.id)}
                                />
                              </td>
                              {newAuto.inputSource?.fileMetadata?.columns.map(col => (
                                <td key={col} className={newAuto.inputSource?.selectedFields.includes(col) ? 'font-semibold' : 'opacity-40 grayscale italic'}>
                                  {row[col] || <span className="text-slate-300">N/A</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <footer className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t dark:border-slate-800 pt-6">
                      <div className="mx-summary text-[10px] font-black uppercase text-slate-500 tracking-widest">
                        <strong className="text-indigo-600 dark:text-indigo-400">{selectedRowIds.size}</strong> rows selected
                        <span className="mx-dot">•</span>
                        <strong className="text-indigo-600 dark:text-indigo-400">{newAuto.inputSource?.selectedFields.length}</strong> columns selected
                      </div>
                      <div className="mx-hint flex items-center text-[9px] font-bold text-slate-400 uppercase italic">
                        <span className="mr-2">💡</span> Selecting only the columns needed improves Tool accuracy and reduces credits.
                      </div>
                    </footer>
                  </div>

                  {/* Output Authority Section */}
                  <div className="mx-card bg-slate-50/50 dark:bg-slate-900/50 border-2 border-dashed border-slate-200 dark:border-slate-800">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">🛠️</span>
                        <strong className="text-sm uppercase font-black tracking-tight">Output Authority</strong>
                      </div>
                      <span className="mx-badge">Derived from last tool</span>
                    </div>

                    <div className="flex flex-wrap gap-8">
                      {availableOutputModes.map(mode => (
                        <label key={mode} className="mx-check flex items-center space-x-3 cursor-pointer group">
                          <input
                            type="radio"
                            name="mxOutputMode"
                            value={mode}
                            checked={newAuto.finalOutputMode === mode}
                            onChange={() => setNewAuto(p => ({ ...p, finalOutputMode: mode }))}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-xs font-black uppercase text-slate-500 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                            {mode === 'json' ? '📦 Structured (JSON)' : mode === 'csv' ? '📊 Table (CSV)' : mode.toUpperCase()}
                          </span>
                        </label>
                      ))}
                      {availableOutputModes.length === 0 && (
                        <p className="text-xs text-slate-400 italic">No output modes available for the current tool sequence.</p>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {wizardStep === 6 && (
                <div className="max-w-4xl mx-auto space-y-12">
                  <div className="text-center">
                    <h3 className="text-3xl font-black uppercase tracking-tight mb-2">6. Output Delivery</h3>
                    <p className="text-slate-500 text-sm">"Final output is defined exclusively by the last tool in your automation."</p>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[3rem] p-12 text-center shadow-inner">
                    <div className="mb-8">
                      <div className="text-5xl mb-4">{finalTool?.icon}</div>
                      <h4 className="text-xl font-black uppercase tracking-tight">{finalTool?.name}</h4>
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1 italic">Protocol Authority Tool</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
                      {availableOutputModes.map(mode => (
                        <button
                          key={mode}
                          onClick={() => setNewAuto(p => ({ ...p, finalOutputMode: mode }))}
                          className={`p-6 border-2 rounded-3xl transition-all flex flex-col items-center justify-center space-y-3 ${newAuto.finalOutputMode === mode ? 'border-indigo-600 bg-white dark:bg-slate-950 shadow-xl scale-105' : 'border-slate-200 dark:border-slate-800 bg-white/40 opacity-60 hover:opacity-100'}`}
                        >
                          <span className="text-2xl">
                            {mode === 'json' ? '📦' : mode === 'csv' ? '📊' : mode === 'email' ? '✉️' : mode === 'webhook' ? '🌐' : '📜'}
                          </span>
                          <span className="text-[10px] font-black uppercase tracking-widest">{mode}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {wizardStep === 7 && (
                <div className="max-w-2xl mx-auto space-y-12">
                  <div className="text-center">
                    <div className="text-7xl mb-6">🛰️</div>
                    <h3 className="text-4xl font-black uppercase tracking-tight leading-none mb-2">Activation Review</h3>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.4em] opacity-60">ShadowEngine Protocol v3.2</p>
                  </div>

                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[3rem] p-12 space-y-8 shadow-sm">
                    <div className="flex items-center justify-between border-b dark:border-slate-800 pb-5">
                      <span className="text-[10px] font-black uppercase text-slate-400">Payload Source</span>
                      <span className="text-xs font-black uppercase">{newAuto.inputSource?.fileMetadata?.name || 'Manual Inbound'}</span>
                    </div>
                    <div className="flex items-center justify-between border-b dark:border-slate-800 pb-5">
                      <span className="text-[10px] font-black uppercase text-slate-400">Execution Intent</span>
                      <span className="text-xs font-black text-indigo-600 uppercase flex items-center">
                        <span className="mr-2">{INTENTS.find(i => i.id === newAuto.intent)?.icon}</span>
                        {INTENTS.find(i => i.id === newAuto.intent)?.name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b dark:border-slate-800 pb-5">
                      <span className="text-[10px] font-black uppercase text-slate-400">Chain Authority</span>
                      <span className="text-xs font-black uppercase text-slate-900 dark:text-white">{finalTool?.name}</span>
                    </div>
                    <div className="flex items-center justify-between border-b dark:border-slate-800 pb-5">
                      <span className="text-[10px] font-black uppercase text-slate-400">Final Format</span>
                      <span className="text-xs font-black uppercase px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded">{newAuto.finalOutputMode}</span>
                    </div>
                  </div>

                  <div className="p-10 bg-indigo-600 text-white rounded-[2.5rem] shadow-2xl shadow-indigo-600/40 text-center relative overflow-hidden">
                    <p className="text-sm font-bold leading-relaxed relative z-10">
                      "I acknowledge this pipeline is deterministic. Execution will consume credits per run according to the chained tool costs."
                    </p>
                  </div>
                </div>
              )}

            </div>

            <div className="px-10 py-8 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/30 dark:bg-slate-900/30 shrink-0">
              <button
                onClick={handleBack}
                disabled={wizardStep === 1 || isUploading}
                className="px-10 py-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600 disabled:opacity-0 transition-all"
              >Back</button>

              <div className="flex items-center space-x-5">
                {wizardStep === 7 ? (
                  <button
                    onClick={() => setIsWizardOpen(false)}
                    className="px-16 py-5 bg-indigo-600 text-white font-black text-xs rounded-2xl uppercase tracking-[0.2em] shadow-2xl shadow-indigo-600/40 active:scale-95 hover:scale-105 transition-all"
                  >Enable Protocol</button>
                ) : (
                  <button
                    onClick={handleNext}
                    disabled={
                      isUploading ||
                      (wizardStep === 2 && !newAuto.inputSource?.type) ||
                      (wizardStep === 3 && !newAuto.intent) ||
                      (wizardStep === 4 && !newAuto.steps?.length) ||
                      (wizardStep === 6 && !newAuto.finalOutputMode)
                    }
                    className="px-14 py-5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black text-[10px] rounded-2xl uppercase tracking-[0.2em] disabled:opacity-20 transition-all shadow-xl hover:scale-105"
                  >Continue Path</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Automations;
